import React, { ChangeEvent, useMemo, useCallback, useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogTitle, Typography } from '@material-ui/core';
import { Button } from '@material-ui/core';
import {
  SaveSurvey,
  SaveSurveyRequest,
  usePostponeSurveyMutation,
  useSaveSurveyMutation,
} from 'src/modules/shared/slices/survey/surveyApi';
import { StyledButtonLink } from '../components/styled';
import { useRequestMeMutation } from 'src/modules/shared/slices/auth/entity/api/auth';
import { answerInitialValue, getFormInitialValues } from './components/constants';
import { User } from 'src/config/api/models/User';
import { useSnackbar } from 'notistack';
import PostPoneConfirmationDialog from './components/PostPoneConfirmationDialog/PostPoneConfirmationDialog';
import SurveyFormContent from './components/SurveyFormContent/SurveyFormContent';

const Survey = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [saveSurvey, { isLoading: saveLoading, isSuccess: saveSuccess, isError: saveError }] =
    useSaveSurveyMutation({
      fixedCacheKey: 'survey',
    });

  const [dismissSurvey, { isSuccess: dismissSuccess, isError: dismissError }] =
    usePostponeSurveyMutation({ fixedCacheKey: 'postpone-confirmation-dialog' });

  const [, { data: meResponse }] = useRequestMeMutation({
    fixedCacheKey: '/me',
  });

  const [infoModal, setShowInfoModal] = useState<NonNullable<User['survey']>['isAnswered']>(
    !!!meResponse?.survey?.isAnswered
  );
  const [form, setForm] = useState<SaveSurvey[]>(
    getFormInitialValues(meResponse?.survey?.questions)
  );
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);

  const isFormValid = useMemo(() => {
    return !form.some(
      (item) =>
        item.answer === answerInitialValue ||
        item.multiChoiceAnswer === answerInitialValue.toString()
    );
  }, [form]);

  const isFormDirty = useMemo(() => {
    return form.some(
      (item) =>
        (item.answer !== undefined && item.answer !== answerInitialValue) ||
        (item.multiChoiceAnswer !== undefined &&
          item.multiChoiceAnswer !== answerInitialValue.toString() &&
          item.multiChoiceAnswer !== '')
    );
  }, [form]);

  const handleCloseModal = useCallback(() => {
    setShowInfoModal(false);
  }, []);

  const handleCloseConfirmationModal = useCallback(() => {
    setConfirmationModal(false);
  }, []);

  const handleSubmit = useCallback(
    (e: ChangeEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!isFormValid) return;
      if (!meResponse?.survey?.key) return;

      const filteredData: SaveSurveyRequest[] = form.map(({ key, answer, multiChoiceAnswer }) =>
        answer !== undefined ? { key, answer } : { key, multiChoiceAnswer }
      );

      saveSurvey({ key: meResponse?.survey?.key, payload: filteredData });
    },
    [form, isFormValid, meResponse?.survey?.key, saveSurvey]
  );

  const handleDismissModal = useCallback(() => {
    if (isFormDirty && !confirmationModal) {
      return setConfirmationModal(isFormDirty);
    }
    handleCloseModal();
    if (!meResponse?.survey?.key) return;
    dismissSurvey({ key: meResponse?.survey?.key });
  }, [confirmationModal, dismissSurvey, handleCloseModal, isFormDirty, meResponse?.survey?.key]);

  useEffect(() => {
    if (saveSuccess) {
      enqueueSnackbar('Bedankt voor je reactie. Hiermee kunnen we je ervaring steeds verbeteren.', {
        variant: 'success',
        style: { whiteSpace: 'pre-line' },
      });
      handleCloseModal();
    }
  }, [handleCloseModal, saveSuccess, enqueueSnackbar]);

  useEffect(() => {
    if (dismissSuccess) {
      enqueueSnackbar('Succesvol uitgesteld. We spreken elkaar snel weer.', {
        variant: 'success',
        style: { whiteSpace: 'pre-line' },
      });
      handleCloseModal();
    }
  }, [dismissSuccess, enqueueSnackbar, handleCloseModal]);

  useEffect(() => {
    if (saveError || dismissError) {
      enqueueSnackbar('Er is iets misgegaan. Probeer het later opnieuw.', {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
      });
      handleCloseModal();
    }
  }, [dismissError, handleCloseModal, saveError, enqueueSnackbar]);

  if (!meResponse?.survey) return null;
  const { title } = meResponse.survey;

  return (
    <>
      <Dialog open={infoModal}>
        <form onSubmit={handleSubmit}>
          <DialogTitle style={{ paddingBottom: 0 }}>
            <Typography variant="h3" style={{ marginBottom: 0 }}>
              {title}
            </Typography>
          </DialogTitle>
          <SurveyFormContent form={form} setForm={setForm} />
          <DialogActions style={{ justifyContent: 'space-between' }}>
            <Button
              disabled={!isFormValid || saveLoading}
              type="submit"
              variant="contained"
              color="primary"
              disableElevation
              style={{ textTransform: 'none' }}
            >
              Stuur feedback
            </Button>
            <StyledButtonLink variant="text" onClick={() => handleDismissModal()}>
              Sluit en vraag morgen opnieuw
            </StyledButtonLink>
          </DialogActions>
        </form>
      </Dialog>
      {confirmationModal && (
        <PostPoneConfirmationDialog
          onClose={handleCloseConfirmationModal}
          onSubmit={handleDismissModal}
        />
      )}
    </>
  );
};

export default Survey;
